import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const ease = [0.16, 1, 0.3, 1];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OCCASIONS = ["Dinner Party", "Wedding", "Gallery Opening", "Corporate", "Personal Cellar", "Other"];
const CUBES = ["The King Cube", "The Diamond", "The Shard", "Assorted Quartet"];

const initialForm = {
    name: "",
    email: "",
    phone: "",
    occasion: "",
    cube_preference: "",
    quantity: "",
    message: "",
};

export default function Concierge() {
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.occasion || !form.cube_preference) {
            toast.error("Please complete name, email, occasion and cube selection.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/concierge`, form);
            setSubmitted(true);
            setForm(initialForm);
            toast.success("Request received. Our concierge will reply within 24 hours.");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Unable to submit right now. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="concierge"
            data-testid="concierge-section"
            className="relative bg-obsidian-900 py-24 md:py-32 lg:py-40 overflow-hidden"
        >
            {/* Side image */}
            <div className="absolute right-0 top-0 bottom-0 hidden lg:block w-[38%] opacity-40">
                <img
                    src="https://images.pexels.com/photos/18454180/pexels-photo-18454180.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200"
                    alt=""
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-obsidian-900 via-obsidian-900/70 to-transparent" />
            </div>

            <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
                <div className="grid grid-cols-12 gap-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease }}
                        className="col-span-12 lg:col-span-5"
                    >
                        <span className="overline">§ 03 — Concierge</span>
                        <h2 className="mt-6 font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
                            A quiet <br />
                            <span className="italic text-amber-glow">request.</span>
                        </h2>
                        <p className="mt-8 text-ice-200/70 leading-relaxed max-w-md">
                            Tell us the occasion, the spirit, the number of guests. Our concierge replies within
                            twenty-four hours with a proposal and a private courier window.
                        </p>

                        <div className="mt-12 space-y-4 text-sm text-ice-400">
                            <div className="flex items-center gap-3" data-testid="concierge-contact-email">
                                <span className="w-6 h-px bg-amber-glow" /> support@icefarmers.com
                            </div>
                        </div>
                    </motion.div>

                    {/* Form */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease, delay: 0.15 }}
                        className="col-span-12 lg:col-span-7 lg:pl-10"
                    >
                        {submitted ? (
                            <div
                                data-testid="concierge-success"
                                className="border border-amber-glow/40 bg-obsidian-800/60 backdrop-blur-xl p-10 md:p-14"
                            >
                                <span className="font-script text-amber-glow text-4xl">merci.</span>
                                <h3 className="font-serif-display text-3xl md:text-4xl mt-4">
                                    Your request is with our concierge.
                                </h3>
                                <p className="text-ice-200/70 mt-4 leading-relaxed max-w-md">
                                    A reply will arrive within twenty-four hours from a member of our team.
                                    In the meantime, consider what spirit deserves your attention tonight.
                                </p>
                                <button
                                    data-testid="concierge-reset"
                                    onClick={() => setSubmitted(false)}
                                    className="lux-btn lux-btn-ghost mt-10"
                                >
                                    Submit Another
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={onSubmit}
                                className="border border-white/10 bg-obsidian-800/40 backdrop-blur-xl p-8 md:p-12"
                                data-testid="concierge-form"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <Field label="Full Name" required>
                                        <input
                                            data-testid="form-name"
                                            className="lux-input"
                                            value={form.name}
                                            onChange={update("name")}
                                            placeholder="Jean Dupont"
                                            required
                                        />
                                    </Field>
                                    <Field label="Email" required>
                                        <input
                                            data-testid="form-email"
                                            type="email"
                                            className="lux-input"
                                            value={form.email}
                                            onChange={update("email")}
                                            placeholder="you@private.com"
                                            required
                                        />
                                    </Field>
                                    <Field label="Phone">
                                        <input
                                            data-testid="form-phone"
                                            className="lux-input"
                                            value={form.phone}
                                            onChange={update("phone")}
                                            placeholder="Optional"
                                        />
                                    </Field>
                                    <Field label="Quantity">
                                        <input
                                            data-testid="form-quantity"
                                            className="lux-input"
                                            value={form.quantity}
                                            onChange={update("quantity")}
                                            placeholder="e.g. 48 cubes"
                                        />
                                    </Field>
                                    <Field label="Occasion" required>
                                        <select
                                            data-testid="form-occasion"
                                            className="lux-input appearance-none"
                                            value={form.occasion}
                                            onChange={update("occasion")}
                                            required
                                        >
                                            <option value="" className="bg-obsidian-800">
                                                Select
                                            </option>
                                            {OCCASIONS.map((o) => (
                                                <option key={o} value={o} className="bg-obsidian-800">
                                                    {o}
                                                </option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label="Cube" required>
                                        <select
                                            data-testid="form-cube"
                                            className="lux-input appearance-none"
                                            value={form.cube_preference}
                                            onChange={update("cube_preference")}
                                            required
                                        >
                                            <option value="" className="bg-obsidian-800">
                                                Select
                                            </option>
                                            {CUBES.map((c) => (
                                                <option key={c} value={c} className="bg-obsidian-800">
                                                    {c}
                                                </option>
                                            ))}
                                        </select>
                                    </Field>
                                </div>

                                <div className="mt-8">
                                    <Field label="A few words">
                                        <textarea
                                            data-testid="form-message"
                                            className="lux-input resize-none"
                                            rows={3}
                                            value={form.message}
                                            onChange={update("message")}
                                            placeholder="The occasion, the guests, the spirit…"
                                        />
                                    </Field>
                                </div>

                                <div className="mt-12 flex flex-wrap items-center justify-between gap-4">
                                    <p className="text-[0.68rem] uppercase tracking-[0.28em] text-ice-400 max-w-xs">
                                        Submissions are held in strictest confidence.
                                    </p>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="form-submit"
                                        className="lux-btn lux-btn-primary"
                                    >
                                        {submitting ? "Sending…" : "Submit Request"}
                                        {!submitting && <span aria-hidden>→</span>}
                                    </button>
                                </div>
                            </form>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-[0.65rem] uppercase tracking-[0.3em] text-ice-400 mb-1">
                {label}
                {required ? <span className="text-amber-glow"> *</span> : null}
            </span>
            {children}
        </label>
    );
}
